/**
 * ImageUploadService - Handle image uploads for characters and monsters
 *
 * This service manages uploading and saving images to the correct directories
 * with proper naming conventions and conflict resolution.
 *
 * @date_of_change: 2025-10-07 - Returns CANONICAL format only
 * @date_of_change: 2025-10-09 - Use S3 direct upload via sign/PUT/finalize flow
 */


class ImageUploadService {
  /**
   * Upload and save an image for a character or monster
   * @param {File} file - The image file to upload
   * @param {string} name - The character/monster name
   * @param {string} type - 'character' or 'monster'
   * @returns {Promise<string>} - The final image path (canonical format: "uploads/...")
   * @date_of_change: 2025-09-21 - Updated to use backend upload API
   * @date_of_change: 2025-10-03 - Fixed 401 error by using apiFetch for Clerk authentication
   * @date_of_change: 2025-10-09 - Use S3 sign/PUT/finalize flow for direct S3 uploads
   */
  static async uploadImage(file, name, type) {
    try {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload PNG, JPG, or WebP files only.');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('File size too large. Please upload images smaller than 5MB.');
      }

      const { assertAssetUploadAllowed } = await import('./EntitlementService.js');
      await assertAssetUploadAllowed({ fileSize: file.size });

      // Map type to S3 asset type ('token' for both characters and monsters)
      const assetType = 'token';

      console.log(`[ImageUploadService] Uploading ${file.name} for ${type}: ${name} to S3`);

      // Use CustomAssetManager's S3 upload flow (sign → PUT → finalize)
      const customAssetManager = (await import('./CustomAssetManager.js')).default;
      const asset = await customAssetManager.uploadToS3ViaSignedUrl(file, { type: assetType });

      // asset.key is in format: "uploads/{userId}/token/{timestamp}-{random}.{ext}"
      const canonicalKey = asset.key;

      if (!canonicalKey || !canonicalKey.startsWith('uploads/')) {
        throw new Error(`S3 upload returned invalid key format: ${canonicalKey}`);
      }

      console.log(`[ImageUploadService] S3 upload successful: ${canonicalKey}`);
      return canonicalKey;

    } catch (error) {
      console.error('[ImageUploadService] Upload failed:', error);
      throw error;
    }
  }

  /**
   * Get file extension from MIME type
   * @param {string} mimeType - The MIME type
   * @returns {string} - File extension
   */
  static getFileExtension(mimeType) {
    const extensions = {
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/svg+xml': '.svg'
    };
    return extensions[mimeType] || '.png';
  }

  /**
   * Clean filename by removing invalid characters
   * @param {string} name - Original name
   * @returns {string} - Clean filename
   */
  static cleanFileName(name) {
    return name
      .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .trim();
  }

}

export default ImageUploadService;
